<script setup lang="ts">
import { LightRaysBackground } from '@cloudintellitech/ui'
import dayjs from '@/utils/dayjs'
import { ua } from '@/utils/ua'
import Provider from './ui/provider/index.vue'
import 'dayjs/locale/en'
import 'dayjs/locale/zh-cn'
import 'dayjs/locale/zh-tw'

const route = useRoute()

const settingsStore = useSettingsStore()
const backgroundColorScheme = computed(() => (
  settingsStore.currentColorScheme === 'dark' ? 'dark' : 'light'
))

const { locale } = useI18n()
const { auth } = useAuth()
const { generateI18nTitle } = useMenu()

document.body.setAttribute('data-os', ua.getOS().name || '')

const isAuth = computed(() => {
  return route.matched.every((item) => {
    return auth(item.meta.auth ?? '')
  })
})

// 设置网页 title
watch([
  () => settingsStore.settings.app.enableDynamicTitle,
  () => settingsStore.title,
  () => settingsStore.customTitleList,
  () => settingsStore.lang,
], () => {
  nextTick(() => {
    if (settingsStore.settings.app.enableDynamicTitle && settingsStore.title) {
      const title = settingsStore.customTitleList.find(item => item.fullPath === route.fullPath)?.title || generateI18nTitle(settingsStore.title)
      document.title = `${title} - ${'Fantastic-admin 专业版'}`
    }
    else {
      document.title = 'Fantastic-admin 专业版'
    }
  })
}, {
  immediate: true,
  deep: true,
})

onMounted(() => {
  settingsStore.setMode(document.documentElement.clientWidth)
  window.addEventListener('resize', () => {
    settingsStore.setMode(document.documentElement.clientWidth)
  })
})

watch(() => settingsStore.lang, () => {
  locale.value = settingsStore.lang
})

watch(() => settingsStore.lang, () => {
  switch (settingsStore.lang) {
    case 'zh-cn':
      dayjs.locale('zh-cn')
      break
    case 'zh-tw':
      dayjs.locale('zh-tw')
      break
    case 'en':
      dayjs.locale('en')
      break
  }
}, {
  immediate: true,
})
</script>

<template>
  <Provider>
    <LightRaysBackground
      class="app-light-rays-background"
      :color-scheme="backgroundColorScheme"
      :mouse-influence="0"
      :rays-speed="1.5"
      :saturation="0"
    />
    <div class="app-content-layer">
      <RouterView v-slot="{ Component }">
        <component :is="Component" v-if="isAuth" />
        <FaNotAllowed v-else />
      </RouterView>
      <FaBackToTop />
      <ForceChangePassword />
      <FaLoginAgain />
      <FaCheckUpdates />
    </div>
    <Teleport to="#app-overlays">
      <FaToast />
      <FaNotification />
    </Teleport>
    <Teleport to="#app-safe-areas">
      <div class="app-safe-area-glass app-safe-area-glass--top" aria-hidden="true" />
      <div class="app-safe-area-glass app-safe-area-glass--bottom" aria-hidden="true" />
    </Teleport>
  </Provider>
</template>

<style scoped>
.app-light-rays-background {
  position: fixed;
  inset: 0;
  z-index: 0;
}

.app-content-layer {
  position: relative;
  z-index: 1;
  width: 100%;
  min-height: var(--g-app-content-height);
}

/* 玻璃位于独立视口根层，应用场景和所有网页浮层均在其下方。 */
.app-safe-area-glass {
  --g-safe-area-glass-filter: none;

  position: fixed;
  inset-inline: 0;
  z-index: 2;
  pointer-events: none;
  background: var(--cit-color-bg-surface);

  /* autoprefixer: ignore next */
  -webkit-backdrop-filter: var(--g-safe-area-glass-filter); /* stylelint-disable-line property-no-vendor-prefix -- 旧版 iOS 需要保留 WebKit 前缀。 */
  backdrop-filter: var(--g-safe-area-glass-filter);

  &--top {
    top: 0;
    height: var(--g-safe-area-top);
  }

  &--bottom {
    bottom: 0;
    height: var(--g-safe-area-bottom);
  }
}

@supports ((-webkit-backdrop-filter: blur(1px)) or (backdrop-filter: blur(1px))) {
  .app-safe-area-glass {
    --g-safe-area-glass-filter: blur(var(--cit-effect-glass-blur)) saturate(var(--cit-effect-glass-saturation));

    background: var(--cit-effect-glass-tabs-background);
  }
}

@media (prefers-reduced-transparency: reduce) {
  .app-safe-area-glass {
    --g-safe-area-glass-filter: none;

    background: var(--cit-color-bg-surface);
  }
}

@media (forced-colors: active) {
  .app-safe-area-glass {
    --g-safe-area-glass-filter: none;

    background: Canvas;
  }
}
</style>
